import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import * as amplitude from '@amplitude/analytics-browser';

interface PricingCardProps {
  title: string;
  flag: string;
  monthlyPrice: string;
  yearlyPrice?: string;
  features: string[];
  subscriptionLink: string;
  popular?: boolean;
  delay: number;
}

function PricingCard({ title, flag, monthlyPrice, yearlyPrice, features, subscriptionLink, popular, delay }: PricingCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className={`relative bg-white dark:bg-gray-800 rounded-2xl border transition-all duration-300 hover:shadow-xl group ${
        popular
          ? 'border-emerald-200 dark:border-emerald-700 shadow-lg shadow-emerald-500/10'
          : 'border-gray-100 dark:border-gray-700/50 hover:border-gray-200 dark:hover:border-gray-600'
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-md">
          Le plus populaire
        </div>
      )}

      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">{flag}</span>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>

        <div className="mb-5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {monthlyPrice}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">/ mois</span>
          </div>
          {yearlyPrice && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              soit {yearlyPrice} / an
            </p>
          )}
        </div>

        <ul className="space-y-2.5 mb-6">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
            </li>
          ))}
        </ul>

        <a
          href={subscriptionLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => amplitude.track("Insurance CTA Clicked", { location: "pricing", plan: title })}
          className={`group/btn flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 active:scale-[0.98] ${
            popular
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30'
              : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
          }`}
        >
          <span>Souscrire maintenant</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5" />
        </a>
      </div>
    </motion.div>
  );
}

export function PricingSection() {
  return (
    <section className="py-12 sm:py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-14"
        >
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Une assurance adaptee a votre destination
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Choisissez la formule qui correspond a votre PVT. Sans franchise, sans delai de carence.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <PricingCard
            title="Canada"
            flag="&#x1F1E8;&#x1F1E6;"
            monthlyPrice="31,84 €"
            yearlyPrice="382,08 €"
            features={[
              "Frais medicaux illimites",
              "Teleconsultation incluse",
              "Hospitalisation sans avance",
              "Rapatriement 24h/24",
            ]}
            subscriptionLink="https://www.acs-ami.com/fr/part/pvtistes/globe-pvt/?part=pvtistes&utm_source=pvtistes-pwa&utm_medium=aff-link&utm_campaign=gp-pvt-pwa"
            popular
            delay={0}
          />

          <PricingCard
            title="Autres destinations"
            flag="🌍"
            monthlyPrice="35,70 €"
            yearlyPrice="428,40 €"
            features={[
              "Frais medicaux illimites",
              "Hospitalisation sans avance",
              "Rapatriement 24h/24",
              "Responsabilite civile",
            ]}
            subscriptionLink="https://www.acs-ami.com/fr/part/pvtistes/globe-pvt/?part=pvtistes&utm_source=pvtistes-pwa&utm_medium=aff-link&utm_campaign=gp-pvt-pwa"
            delay={0.1}
          />

          <PricingCard
            title="France & Belgique"
            flag="&#x1F1EB;&#x1F1F7;"
            monthlyPrice="48,70 €"
            yearlyPrice="584,40 €"
            features={[
              "Frais medicaux illimites",
              "Hospitalisation sans avance",
              "Rapatriement 24h/24",
              "Responsabilite civile",
            ]}
            subscriptionLink="https://www.acs-ami.com/fr/part/pvtistes/globe-pvt/?part=pvtistes&utm_source=pvtistes-pwa&utm_medium=aff-link&utm_campaign=gp-pvt-pwa"
            delay={0.2}
          />
        </div>
      </div>
    </section>
  );
}
